import React, { useState } from "react";
import { ethers } from "ethers";
import { getEthersProvider } from "../../web3/provider";
import { getPurchase } from "../../web3/contracts";
import { useToast } from "../common/Toast";
import { fmtUsdt, fmtScn, shortAddress, formatTimestamp } from "../../utils/format";
import SectionHeader from "../common/SectionHeader";

export default function UserLookup() {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const search = async () => {
    const query = q.trim();
    if (!query) { toast.error("Enter an address or user ID."); return; }
    setLoading(true);
    setRes(null);
    try {
      const p = getPurchase(getEthersProvider());

      // Resolve to a user id.
      let id;
      if (ethers.utils.isAddress(query)) {
        id = await p.addressToId(query);
        if (id.isZero()) { toast.error("That address is not registered."); setLoading(false); return; }
      } else if (/^\d+$/.test(query)) {
        id = ethers.BigNumber.from(query);
      } else {
        toast.error("Enter a valid 0x address or a numeric ID.");
        setLoading(false);
        return;
      }

      const [details, packages, income, scn, vip] = await Promise.all([
        p.getUserDetails(id),
        p.getUserPackages(id),
        p.getUserIncomeBreakdown(id),
        p.getUserSCNStats(id),
        p.isUserUnlimitedTier(id),
      ]);

      if (!details.account || details.account === ethers.constants.AddressZero) {
        toast.error("No user found for that ID.");
        setLoading(false);
        return;
      }

      const activePkgs = packages
        .map((b, i) => (b ? i + 1 : 0))
        .filter(Boolean);

      setRes({ id, details, income, scn, vip, activePkgs });
    } catch (e) {
      toast.error("Lookup failed. Check the value and try again.");
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter") search(); };

  return (
    <div className="card">
      <SectionHeader icon="users" title="User Lookup" accent="cyan" />
      <div className="copy-row" style={{ display: "flex", gap: 8 }}>
        <input
          className="input"
          placeholder="Wallet address (0x…) or user ID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
        />
        <button className="btn" style={{ flex: "0 0 auto", padding: "0 18px" }} onClick={search} disabled={loading}>
          {loading ? "…" : "Search"}
        </button>
      </div>

      {res && (
        <div className="lookup-result fade-up">
          <div className="stat-rows">
            <div className="stat-row"><span className="muted">User ID</span><span className="mono">{res.id.toString()}</span></div>
            <div className="stat-row"><span className="muted">Wallet</span><span className="mono">{res.details.account}</span></div>
            <div className="stat-row"><span className="muted">Upline ID</span><span className="mono">{res.details.referrer.toString()}</span></div>
            <div className="stat-row"><span className="muted">Joined</span><span>{formatTimestamp(res.details.joinTime)}</span></div>
            <div className="stat-row"><span className="muted">Active</span><span>{res.details.isActive ? "Yes" : "No"}</span></div>
            <div className="stat-row"><span className="muted">Tier</span><span style={{ color: res.vip ? "var(--gold)" : "var(--text)" }}>{res.vip ? "★ VIP (Unlimited)" : "Standard"}</span></div>
            <div className="stat-row"><span className="muted">Direct Referrals</span><span>{res.details.totalDirectReferrals.toString()}</span></div>
            <div className="stat-row"><span className="muted">Total Income (net)</span><span>{fmtUsdt(res.details.totalIncome)} USDT</span></div>
            <div className="stat-row"><span className="muted">Direct / Generation / Matrix</span><span>{fmtUsdt(res.income.directReferralIncome)} / {fmtUsdt(res.income.normalLevelIncome)} / {fmtUsdt(res.income.matrixLevelIncome)}</span></div>
            <div className="stat-row"><span className="muted">SCN Claimable</span><span>{fmtScn(res.scn.claimable)} SCN</span></div>
            <div className="stat-row"><span className="muted">SCN Claimed</span><span>{fmtScn(res.scn.totalClaimed)} SCN</span></div>
            <div className="stat-row"><span className="muted">Active Packages</span><span>{res.activePkgs.length ? res.activePkgs.map((n) => `#${n}`).join(" ") : "None"}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
